import { AlertTriangle, ArrowRight, FileText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../components/Button';
import { Dialog } from '../../../components/Dialog';
import { Input } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import { formatPriceRON, pluralRO } from '../../../lib/format';
import { useGetOrderQuery, useSplitOrderMutation } from '../orders.api';
import { activeInvoices, orderErrorRO, splitIneligibleReason, splitRows } from '../orders.lib';
import type { Order, SplitOrderResult } from '../orders.schema';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    orderId: number;
    /** Called when the modal closes after a successful split — v1 hosts pass a
     *  refetch/reload here; the v2 host relies on RTK tag invalidation. */
    onSuccessClose?: (newOrderId: number) => void;
};

const newOrderHref = (id: number) =>
    window.location.pathname.startsWith('/v2') ? `/v2/orders/${id}` : `/orders/${id}`;

// What the split will do to invoicing/payment — shown BEFORE confirming so the
// operator knows exactly what changes.
function EffectsList({ order }: { order: Order }) {
    const active = activeInvoices(order);
    const fiscal = active.filter((i) => i.type === 'invoice' || i.type === 'advance');
    const proformas = active.filter((i) => i.type === 'proforma');
    const handler = order.paymentType?.handler;

    const effects: { text: string; warn?: boolean }[] = [];

    for (const inv of fiscal) {
        const label = [inv.series, inv.number].filter((x) => x && x !== 'TMP' && x !== 0).join(' ') || `#${inv.id}`;
        effects.push({
            text: `Factura ${label} va fi stornata; se emit facturi noi pentru ambele comenzi (storno si facturile noi se sincronizeaza automat in SmartBill).`,
        });
    }
    for (const inv of proformas) {
        effects.push({ text: `Proforma #${inv.number || inv.id} va fi anulata; se emit proforme noi pentru ambele comenzi.` });
    }
    if (active.length === 0) {
        effects.push({ text: 'Nu exista facturi emise — nu se genereaza documente la impartire.' });
    }

    if (order.paymentStatus === 'paid') {
        effects.push({
            text: `Plata este deja incasata${order.cardPaymentReference ? ` (ref. ${order.cardPaymentReference})` : ''} si acopera ambele comenzi — amandoua raman marcate ca platite.`,
        });
    } else if (handler === 'card-gateway') {
        effects.push({
            text: 'Linkul de plata trimis are valoarea veche — dupa impartire retrimite linkul de plata (se regenereaza cu noul total).',
            warn: true,
        });
    } else if (handler === 'direct-delivery') {
        effects.push({ text: 'Fiecare comanda va incasa ramburs propria valoare la livrare.' });
    } else if (handler === 'bank-transfer') {
        effects.push({ text: `Referinta de transfer${order.bankPaymentReference ? ` ${order.bankPaymentReference}` : ''} ramane valabila pentru ambele comenzi.` });
    }

    return (
        <ul className="space-y-1">
            {effects.map((e, i) => (
                <li key={i} className={`flex gap-2 text-xs ${e.warn ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    {e.warn ? <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" /> : <FileText aria-hidden="true" className="h-4 w-4 shrink-0" />}
                    <span>{e.text}</span>
                </li>
            ))}
        </ul>
    );
}

function ResultPanel({ result, onClose }: { result: SplitOrderResult; onClose: () => void }) {
    const { newOrder, invoicing, invoicingError } = result;
    return (
        <div className="space-y-4">
            <p className="text-sm text-gray-900 dark:text-gray-100">
                Comanda a fost impartita. Comanda noua: <strong>#{newOrder.orderNumber}</strong>
            </p>
            <ul className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                {invoicing.stornoed.map((s) => (
                    <li key={`s-${s.invoiceId}`}>
                        Storno emis pentru factura {[s.series, s.number].filter((x) => x && x !== 'TMP' && x !== 0).join(' ') || `#${s.invoiceId}`} — se sincronizeaza automat in SmartBill.
                    </li>
                ))}
                {invoicing.voided.map((v) => (
                    <li key={`v-${v.invoiceId}`}>Proforma #{v.invoiceId} a fost anulata.</li>
                ))}
                {invoicing.created.map((c) => (
                    <li key={`c-${c.invoiceId}`}>
                        {c.type === 'proforma' ? 'Proforma noua' : c.type === 'advance' ? 'Factura de avans noua' : 'Factura noua'} pentru comanda{' '}
                        {c.orderId === newOrder.id ? `#${newOrder.orderNumber}` : 'curenta'} (numarul se aloca la sincronizarea SmartBill).
                    </li>
                ))}
            </ul>
            {invoicingError && (
                <p className="flex gap-2 text-xs text-red-600 dark:text-red-400">
                    <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
                    Refacturarea automata a esuat ({invoicingError}). Comenzile au fost impartite corect — emite documentele manual din paginile comenzilor.
                </p>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
                <Button asChild variant="primary" className="flex-1">
                    <a href={newOrderHref(newOrder.id)}>
                        Deschide comanda noua <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4 shrink-0" />
                    </a>
                </Button>
                <Button variant="secondary" className="flex-1" onClick={onClose}>
                    Inchide
                </Button>
            </div>
        </div>
    );
}

/**
 * Imparte comanda — moves chosen quantities onto a brand-new order. Feedback is
 * rendered inline (not only toasts) because this dialog is also mounted on v1
 * pages, where the sonner <Toaster> does not exist.
 */
export function SplitOrderModal({ open, onOpenChange, orderId, onSuccessClose }: Props) {
    const { data: order, isLoading } = useGetOrderQuery(orderId, { skip: !open });
    const [split, { isLoading: splitting }] = useSplitOrderMutation();

    const [moves, setMoves] = useState<Record<string, number>>({});
    const [shippingTarget, setShippingTarget] = useState<'source' | 'new'>('source');
    const [comment, setComment] = useState('');
    const [result, setResult] = useState<SplitOrderResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setMoves({});
            setShippingTarget('source');
            setComment('');
            setResult(null);
            setError(null);
        }
    }, [open, orderId]);

    const rows = useMemo(() => splitRows(order), [order]);
    const ineligible = order ? splitIneligibleReason(order) : null;

    const shipping = Number(order?.shippingCost ?? 0);
    const movedUnits = rows.reduce((s, r) => s + (moves[r.key] ?? 0), 0);
    const totalUnits = rows.reduce((s, r) => s + r.available, 0);
    const movedValue = rows.reduce((s, r) => s + (moves[r.key] ?? 0) * r.unitPrice, 0);
    const remainingValue = rows.reduce((s, r) => s + (r.available - (moves[r.key] ?? 0)) * r.unitPrice, 0);
    const sourceTotal = remainingValue + (shippingTarget === 'source' ? shipping : 0);
    const newTotal = movedValue + (shippingTarget === 'new' ? shipping : 0);
    const valid = movedUnits > 0 && movedUnits < totalUnits;

    const setMove = (key: string, max: number, raw: string) => {
        const q = Math.max(0, Math.min(max, Math.trunc(Number(raw) || 0)));
        setMoves((m) => ({ ...m, [key]: q }));
    };

    const close = (next: boolean) => {
        onOpenChange(next);
        if (!next && result) onSuccessClose?.(result.newOrder.id);
    };

    const submit = async () => {
        setError(null);
        try {
            const picked = rows.filter((r) => (moves[r.key] ?? 0) > 0);
            const res = await split({
                id: orderId,
                body: {
                    products: picked.filter((r) => r.kind === 'product').map((r) => ({ id: r.rowId, quantity: moves[r.key] })),
                    cards: picked.filter((r) => r.kind === 'card').map((r) => ({ id: r.rowId, quantity: moves[r.key] })),
                    shippingTarget,
                    comment: comment.trim() || undefined,
                },
            }).unwrap();
            setResult(res);
            toast.success(`Comanda a fost impartita — noua comanda #${res.newOrder.orderNumber}`);
        } catch (e) {
            const msg = orderErrorRO((e as { message?: string })?.message);
            setError(msg);
            toast.error(msg);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={close}
            title="Imparte comanda"
            description={result ? undefined : 'Muta o parte din produse pe o comanda noua. Clientul, adresele si plata se copiaza; facturile se storneaza si se reemit automat.'}
            maxWidth="max-w-2xl"
            closeOnBackdrop={false}
            footer={
                result ? undefined : (
                    <>
                        <Button variant="secondary" onClick={() => close(false)} disabled={splitting}>
                            Anuleaza
                        </Button>
                        <Button variant="danger" onClick={submit} disabled={!valid || splitting || !!ineligible || isLoading}>
                            {splitting ? 'Se imparte...' : 'Imparte comanda'}
                        </Button>
                    </>
                )
            }
        >
            {isLoading || !order ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Se incarca comanda...</p>
            ) : result ? (
                <ResultPanel result={result} onClose={() => close(false)} />
            ) : ineligible ? (
                <p className="flex gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <AlertTriangle aria-hidden="true" className="h-5 w-5 shrink-0" /> {ineligible}
                </p>
            ) : (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Cantitati de mutat pe comanda noua</span>
                        <div className="divide-y divide-gray-200 rounded-md border border-solid border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                            {rows.map((r) => (
                                <div key={r.key} className="flex items-center gap-3 p-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm text-gray-900 dark:text-gray-100">{r.label}</p>
                                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                                            {r.secondary ? `${r.secondary} · ` : ''}
                                            {formatPriceRON(r.unitPrice)} × {r.available}
                                        </p>
                                    </div>
                                    <Input
                                        type="number"
                                        min={0}
                                        max={r.available}
                                        value={String(moves[r.key] ?? 0)}
                                        onChange={(e) => setMove(r.key, r.available, e.target.value)}
                                        className="w-20 shrink-0"
                                        aria-label={`Cantitate de mutat pentru ${r.label}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {shipping > 0 && (
                        <div className="space-y-2">
                            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                                Transport ({formatPriceRON(shipping)})
                            </span>
                            <Segmented<'source' | 'new'>
                                value={shippingTarget}
                                onChange={setShippingTarget}
                                size="md"
                                options={[
                                    { value: 'source', label: 'Ramane pe comanda curenta' },
                                    { value: 'new', label: 'Se muta pe comanda noua' },
                                ]}
                            />
                        </div>
                    )}

                    <label className="block space-y-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                        <span>Motiv / comentariu (optional)</span>
                        <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="ex. lipsa stoc, livrare separata" />
                    </label>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-md border border-solid border-gray-200 p-3 dark:border-gray-700">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Comanda curenta (dupa)</p>
                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{pluralRO(totalUnits - movedUnits, 'bucata', 'bucati')}</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatPriceRON(sourceTotal)}</p>
                        </div>
                        <div className="rounded-md border border-solid border-gray-200 p-3 dark:border-gray-700">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Comanda noua</p>
                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{pluralRO(movedUnits, 'bucata', 'bucati')}</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatPriceRON(newTotal)}</p>
                        </div>
                    </div>

                    <EffectsList order={order} />

                    {movedUnits > 0 && movedUnits >= totalUnits && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">Nu poti muta toate produsele — comanda sursa ar ramane goala.</p>
                    )}
                    {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                </div>
            )}
        </Dialog>
    );
}
